// Pool bid sheet: one question set per job type, turned into bid lines from the
// pricing tables. Pure functions only (no Supabase, no React) so it can be tested.
//
// Line descriptions follow the conventions the bid_lines trigger reads:
//   "ROUND INSTALL - 24' Round", "OVAL LINER - 15x30 Oval Liner"  -> primary pool line
//   "Extra: Deep End" / "Extra: Sand Delivery" / "Extra: Dirt Haul ..." /
//   "Extra: Water Delivery" / "Extra: Main Drain" / "TDI: ..."     -> scope flags

export var SHEET_TYPES = [
  { key: 'ag_liner', label: 'Above Ground Liner', bidType: 'above_ground', blurb: 'Liner swap on an existing above-ground pool' },
  { key: 'ag_install', label: 'Above Ground Install', bidType: 'above_ground', blurb: 'New install or tear-down and replace' },
  { key: 'ig_install', label: 'Inground Install', bidType: 'inground_residential', blurb: 'Vinyl liner inground build (opens the inground calculator)' },
  { key: 'ig_liner', label: 'Inground Liner', bidType: 'inground_residential', blurb: 'Liner replacement on an inground pool' },
  { key: 'fiberglass', label: 'Fiberglass Install', bidType: 'inground_residential', blurb: 'Fiberglass shell install' },
  { key: 'landscaping', label: 'Landscaping', bidType: 'landscaping', blurb: 'Restoration, surrounds, walls, drainage' },
]

export function sheetTypeByKey(key) {
  return SHEET_TYPES.find(function(t) { return t.key === key }) || null
}

// ---- helpers ---------------------------------------------------------------

function num(v) { var n = parseFloat(v); return isFinite(n) ? n : 0 }

export function priceOf(pricing, category, label) {
  var row = (pricing || []).find(function(p) { return p.category === category && p.size_label === label })
  return row ? num(row.price) : null
}

function line(description, qty, unit, unitPrice, extra) {
  var q = qty == null ? 1 : qty
  var u = unitPrice == null ? 0 : unitPrice
  return Object.assign({
    description: description,
    category: 'pool',
    quantity: q,
    unit: unit || 'ea',
    unit_cost: Math.round(u * 100) / 100,
    total_cost: Math.round(q * u * 100) / 100,
    unpriced: unitPrice == null || unitPrice === 0,
  }, extra || {})
}

function noteLine(text) {
  return { description: text, category: 'notes', quantity: 1, unit: 'ea', unit_cost: 0, total_cost: 0, unpriced: false, note: true }
}

// Above-ground sizes come straight from the install price list
export function agSizes(pricing) {
  var rows = (pricing || []).filter(function(p) { return p.category === 'round_install' || p.category === 'oval_install' })
  return rows.map(function(p) { return p.size_label }).sort(sizeSort)
}

function sizeSort(a, b) {
  var ar = /round/i.test(a), br = /round/i.test(b)
  if (ar !== br) return ar ? -1 : 1
  var an = a.match(/\d+/g).map(Number), bn = b.match(/\d+/g).map(Number)
  return (an[0] - bn[0]) || ((an[1] || 0) - (bn[1] || 0))
}

function agShape(size) { return /round/i.test(size || '') ? 'ROUND' : 'OVAL' }

var DECK_LABELS = { section: 'Deck - 1 Section', quarter: 'Deck - 1/4 Around', half: 'Deck - 1/2 Around', three_quarter: 'Deck - 3/4 Around', full: 'Deck - Full Around' }
var RAIL_LABELS = { over: 'Top Rail - Deck Over Rail', flush: 'Top Rail - Deck Flush', under: 'Top Rail - Deck Under Rail' }

function deckLines(pricing, a) {
  if (!a.deck || a.deck === 'none') return []
  var out = [line(DECK_LABELS[a.deck], 1, 'ea', priceOf(pricing, 'ag_deck', DECK_LABELS[a.deck]))]
  if (a.deck_rail) out.push(line(RAIL_LABELS[a.deck_rail], 1, 'ea', priceOf(pricing, 'ag_deck', RAIL_LABELS[a.deck_rail])))
  return out
}

var DECK_Q = { key: 'deck', label: 'Deck around the pool', type: 'choice', options: [
  ['none', 'No deck'], ['section', '1 section'], ['quarter', '1/4'], ['half', '1/2'], ['three_quarter', '3/4'], ['full', 'Full'] ] }
var RAIL_Q = { key: 'deck_rail', label: 'Deck vs. top rail', type: 'choice', options: [['over', 'Over'], ['flush', 'Flush'], ['under', 'Under']],
  showIf: function(a) { return a.deck && a.deck !== 'none' } }

// ---- question sets -----------------------------------------------------------
// Question types: choice (buttons), select, number, yesno, text, multi, model (fiberglass), addons

export function questionsFor(type, ctx) {
  var sizes = agSizes(ctx.pricing)
  switch (type) {
    case 'ag_liner': return [
      { key: 'size', label: 'Pool size', type: 'select', options: sizes.map(function(s) { return [s, s] }), required: true },
      { key: 'liner_style', label: 'Liner style', type: 'choice', options: [['overlap', 'Overlap'], ['beaded', 'Beaded'], ['unibead', 'Uni-bead'], ['expandable', 'Expandable (deep end)']] },
      DECK_Q, RAIL_Q,
      { key: 'drain', label: 'Who drains the pool', type: 'choice', options: [['customer', 'Customer'], ['us', 'We drain']] },
      { key: 'damage', label: 'Wall, rail, or bottom-track damage seen?', type: 'yesno' },
      { key: 'damage_note', label: 'Damage notes', type: 'text', showIf: function(a) { return a.damage === true } },
      { key: 'water', label: 'Water fill', type: 'choice', options: [['customer', 'Customer fills'], ['delivery', 'Water delivery']] },
    ]
    case 'ag_install': return [
      { key: 'size', label: 'Pool size', type: 'select', options: sizes.map(function(s) { return [s, s] }), required: true },
      { key: 'model', label: 'Brand / model', type: 'text' },
      { key: 'supplied_by', label: 'Who supplies the pool', type: 'choice', options: [['store', 'Store'], ['customer', 'Customer'], ['us', 'CP Quality']] },
      { key: 'existing', label: 'New install or tear-down?', type: 'choice', options: [['new', 'New install'], ['teardown', 'Tear down existing']] },
      { key: 'grade_ok', label: 'Ground within 6" of grade?', type: 'yesno' },
      { key: 'surface', label: 'Ground is', type: 'choice', options: [['grass', 'Grass'], ['dirt', 'Dirt'], ['footprint', 'Old pool footprint']] },
      { key: 'depth', label: 'Depth', type: 'choice', options: [['flat', 'Flat'], ['deep_end', 'Deep end'], ['half_sunk', 'Half-sunk'], ['semi', 'Semi-inground']] },
      { key: 'dirt', label: 'Dirt', type: 'choice', options: [['leave', 'Leave on site'], ['haul', 'Haul away']] },
      { key: 'sand', label: 'Mason sand', type: 'choice', options: [['customer', 'Customer delivers'], ['us', 'We supply']] },
      DECK_Q, RAIL_Q,
      { key: 'machine', label: 'Machine access', type: 'choice', options: [['yes', 'Machine can get in'], ['hand', 'Hand dig only']] },
      { key: 'electrical', label: 'Electrical', type: 'choice', options: [['customer', "Customer's electrician"], ['us', 'Our hookup']] },
      { key: 'extras', label: 'Other extras', type: 'multi', options: [['main_drain', 'Main drain'], ['heater', 'Heater hookup']] },
    ]
    case 'ig_liner': return [
      { key: 'size', label: 'Pool size / shape', type: 'select', options: (ctx.igLiners || []).map(function(r) { return [r.id, (r.shape ? r.shape + ' ' : '') + r.size] }).concat([['custom', 'Custom / measure on site']]), required: true },
      { key: 'liner_sqft', label: 'Liner sq ft (custom size)', type: 'number', showIf: function(a) { return a.size === 'custom' } },
      { key: 'track', label: 'Track', type: 'choice', options: [['ok', 'Reuse'], ['replace', 'Replace']] },
      { key: 'track_ft', label: 'Track to replace (ln ft)', type: 'number', showIf: function(a) { return a.track === 'replace' } },
      { key: 'coping', label: 'Coping replacement?', type: 'yesno' },
      { key: 'coping_ft', label: 'Coping (ln ft)', type: 'number', showIf: function(a) { return a.coping === true } },
      { key: 'floor', label: 'Floor repair', type: 'choice', options: [['none', 'None'], ['verm', 'Vermiculite'], ['grout', 'Grout patch']] },
      { key: 'fittings', label: 'Fittings', type: 'multi', options: [['faceplates', 'Faceplates & gaskets'], ['light', 'Light niche reseal'], ['steps', 'Vinyl over steps']] },
      { key: 'drain', label: 'Drain & refill', type: 'choice', options: [['customer', 'Customer'], ['us', 'We handle it']] },
      { key: 'water', label: 'Water delivery needed?', type: 'yesno' },
    ]
    case 'fiberglass': return [
      { key: 'model_id', label: 'Model', type: 'model', required: true },
      { key: 'depth_shallow', label: 'Shallow depth (ft)', type: 'number', placeholderFrom: 'depth_shallow_ft' },
      { key: 'depth_deep', label: 'Deep depth (ft)', type: 'number', placeholderFrom: 'depth_deep_ft' },
      { key: 'crane', label: 'Crane needed?', type: 'yesno' },
      { key: 'dirt', label: 'Excavated dirt', type: 'choice', options: [['leave', 'Leave on site'], ['haul', 'Haul away']] },
      { key: 'haul_loads', label: 'Loads to haul', type: 'number', showIf: function(a) { return a.dirt === 'haul' } },
      { key: 'backfill', label: 'Backfill', type: 'choice', options: [['pea', 'Pea gravel'], ['stone', 'Stone / other']] },
      { key: 'silt_ft', label: 'Silt fence (ln ft)', type: 'number' },
      { key: 'spa', label: 'Spa', type: 'choice', options: [['none', 'No spa'], ['spillover', 'Spillover spa'], ['standalone', 'Standalone spa']] },
      { key: 'addons', label: 'Equipment & add-ons', type: 'addons' },
      { key: 'permit', label: 'Permit', type: 'choice', options: [['us', 'We pull it'], ['homeowner', 'Homeowner']] },
      { key: 'barrier_ft', label: 'Barrier / fence (ln ft)', type: 'number' },
    ]
    case 'landscaping': return [
      { key: 'scope', label: 'Scope', type: 'multi', options: [['restoration', 'Restoration after install'], ['surround', 'Full pool surround'], ['wall', 'Retaining wall'], ['plantings', 'Plantings']] },
      { key: 'grading_sqft', label: 'Grading (sq ft)', type: 'number' },
      { key: 'topsoil_yd', label: 'Topsoil (yards)', type: 'number' },
      { key: 'cover', label: 'Seed or sod', type: 'choice', options: [['none', 'Neither'], ['seed', 'Seed'], ['sod', 'Sod']] },
      { key: 'cover_sqft', label: 'Seed / sod area (sq ft)', type: 'number', showIf: function(a) { return a.cover === 'seed' || a.cover === 'sod' } },
      { key: 'wall_ft', label: 'Retaining wall (ln ft)', type: 'number', showIf: function(a) { return (a.scope || []).indexOf('wall') >= 0 } },
      { key: 'deck_type', label: 'Decking', type: 'choice', options: [['none', 'None'], ['concrete', 'Concrete'], ['paver', 'Paver']] },
      { key: 'deck_sqft', label: 'Decking (sq ft)', type: 'number', showIf: function(a) { return a.deck_type === 'concrete' || a.deck_type === 'paver' } },
      { key: 'drainage', label: 'Drainage', type: 'choice', options: [['none', 'None'], ['swale', 'Swale'], ['french', 'French drain']] },
      { key: 'french_ft', label: 'French drain (ln ft)', type: 'number', showIf: function(a) { return a.drainage === 'french' } },
    ]
    default: return []
  }
}

export function visibleQuestions(type, ctx, answers) {
  return questionsFor(type, ctx).filter(function(q) { return !q.showIf || q.showIf(answers || {}) })
}

export function missingRequired(type, ctx, answers) {
  return visibleQuestions(type, ctx, answers).filter(function(q) {
    var v = (answers || {})[q.key]
    return q.required && (v == null || v === '')
  }).map(function(q) { return q.label })
}

// ---- line builders -----------------------------------------------------------

function extraLine(pricing, label, descr) {
  return line(descr || ('Extra: ' + label), 1, 'ea', priceOf(pricing, 'extra', label))
}

function addonByName(ctx, name) {
  return (ctx.addons || []).find(function(x) { return x.name === name })
}

function addonLine(addon, qty) {
  var unit = addon.pricing_type === 'flat' ? 'ea' : (addon.unit_label || 'ea')
  return line(addon.name, addon.pricing_type === 'flat' ? 1 : num(qty), unit, num(addon.customer_price))
}

export function buildLines(type, answers, ctx) {
  var a = answers || {}
  var P = ctx.pricing || []
  var out = []
  switch (type) {
    case 'ag_liner': {
      if (!a.size) return out
      var shape = agShape(a.size)
      var linerLabel = a.size + ' Liner'
      out.push(line(shape + ' LINER - ' + linerLabel, 1, 'ea', priceOf(P, shape.toLowerCase() + '_liner', linerLabel)))
      out = out.concat(deckLines(P, a))
      if (a.drain === 'us') out.push(line('We Drain Pool', 1, 'ea', priceOf(P, 'ag_extra', 'We Drain Pool')))
      if (a.damage === true) out.push(line('Wall / Rail / Track Repair' + (a.damage_note ? ' — ' + a.damage_note : ''), 1, 'ea', priceOf(P, 'ag_extra', 'Wall / Rail / Track Repair')))
      if (a.water === 'delivery') out.push(extraLine(P, 'Water Delivery'))
      var notes = []
      if (a.liner_style) notes.push(({ overlap: 'Overlap', beaded: 'Beaded', unibead: 'Uni-bead', expandable: 'Expandable (deep end)' })[a.liner_style] + ' liner')
      if (a.drain === 'customer') notes.push('customer drains pool before arrival')
      if (a.water !== 'delivery') notes.push('customer handles water fill')
      if (notes.length) out.push(noteLine('Scope: ' + notes.join('; ') + '.'))
      return out
    }
    case 'ag_install': {
      if (!a.size) return out
      var sh = agShape(a.size)
      out.push(line(sh + ' INSTALL - ' + a.size, 1, 'ea', priceOf(P, sh.toLowerCase() + '_install', a.size)))
      if (a.supplied_by === 'us') out.push(line('Pool Supplied by CP Quality' + (a.model ? ' — ' + a.model : ''), 1, 'ea', priceOf(P, 'ag_extra', 'Pool Supplied by CP Quality')))
      if (a.existing === 'teardown') out.push(line('TDI: Tear Down & Dispose Old Pool', 1, 'ea', priceOf(P, 'ag_extra', 'Tear Down & Dispose Old Pool')))
      if (a.depth === 'deep_end') out.push(extraLine(P, 'Deep End'))
      if (a.depth === 'half_sunk') out.push(line('Half-Sunk Install', 1, 'ea', priceOf(P, 'ag_extra', 'Half-Sunk Install')))
      if (a.depth === 'semi') out.push(line('Semi-Inground Install', 1, 'ea', priceOf(P, 'ag_extra', 'Semi-Inground Install')))
      if (a.dirt === 'haul') out.push(extraLine(P, 'Dirt Haul Away (7 yards)'))
      if (a.sand === 'us') {
        var std = (ctx.sand || []).find(function(r) { return r.pool_size === a.size })
        out.push(extraLine(P, 'Sand Delivery', 'Extra: Sand Delivery' + (std ? ' (' + std.new_install_yards + ' yd mason sand)' : '')))
      }
      out = out.concat(deckLines(P, a))
      if (a.machine === 'hand') out.push(line('Hand Dig (No Machine Access)', 1, 'ea', priceOf(P, 'ag_extra', 'Hand Dig (No Machine Access)')))
      if (a.electrical === 'us') out.push(line('Electrical Hookup', 1, 'ea', priceOf(P, 'ag_extra', 'Electrical Hookup')))
      ;(a.extras || []).forEach(function(x) {
        if (x === 'main_drain') out.push(extraLine(P, 'Main Drain'))
        if (x === 'heater') out.push(extraLine(P, 'Heater', 'Extra: Heater Hookup'))
      })
      var n = []
      if (a.model && a.supplied_by !== 'us') n.push('Pool: ' + a.model + (a.supplied_by === 'store' ? ' (supplied by store)' : a.supplied_by === 'customer' ? ' (supplied by customer)' : ''))
      if (a.grade_ok === false) n.push('site is more than 6" out of grade — leveling to be confirmed on site')
      if (a.surface === 'footprint') n.push('installing in place of old pool')
      if (a.sand === 'customer') n.push('customer delivers mason sand before install')
      if (a.electrical !== 'us') n.push('electrical hookup by licensed electrician (customer)')
      if (n.length) out.push(noteLine('Scope: ' + n.join('; ') + '.'))
      return out
    }
    case 'ig_liner': {
      if (!a.size) return out
      if (a.size === 'custom') {
        out.push(line('Inground Liner Replacement — custom size', num(a.liner_sqft), 'sq ft', priceOf(P, 'ig_liner_extra', 'Inground Liner (per sq ft of liner)')))
      } else {
        var row = (ctx.igLiners || []).find(function(r) { return r.id === a.size })
        if (row) out.push(line('Inground Liner Replacement — ' + (row.shape ? row.shape + ' ' : '') + row.size, 1, 'ea', num(row.total_price)))
      }
      if (a.track === 'replace') out.push(line('Track Replacement', num(a.track_ft), 'ln ft', priceOf(P, 'ig_liner_extra', 'Track Replacement (per ln ft)')))
      if (a.coping === true) out.push(line('Coping Replacement', num(a.coping_ft), 'ln ft', priceOf(P, 'ig_liner_extra', 'Coping Replacement (per ln ft)')))
      if (a.floor === 'verm') out.push(line('Floor Repair - Vermiculite', 1, 'ea', priceOf(P, 'ig_liner_extra', 'Floor Repair - Vermiculite')))
      if (a.floor === 'grout') out.push(line('Floor Repair - Grout Patch', 1, 'ea', priceOf(P, 'ig_liner_extra', 'Floor Repair - Grout Patch')))
      var fit = { faceplates: 'Faceplates & Gaskets', light: 'Light Niche Reseal', steps: 'Vinyl Over Steps' }
      ;(a.fittings || []).forEach(function(k) { out.push(line(fit[k], 1, 'ea', priceOf(P, 'ig_liner_extra', fit[k]))) })
      if (a.drain === 'us') out.push(line('Drain & Refill', 1, 'ea', priceOf(P, 'ig_liner_extra', 'Drain & Refill')))
      if (a.water === true) out.push(extraLine(P, 'Water Delivery'))
      return out
    }
    case 'fiberglass': {
      var m = (ctx.models || []).find(function(x) { return x.id === a.model_id })
      if (!m) return out
      var shallow = a.depth_shallow || m.depth_shallow_ft, deep = a.depth_deep || m.depth_deep_ft
      out.push(line('Fiberglass Pool Install — ' + (m.manufacturer ? m.manufacturer + ' ' : '') + m.model_name + ' (' + m.width_ft + 'x' + m.length_ft + ', ' + shallow + "'–" + deep + "' deep)", 1, 'ea', num(m.base_install_price)))
      if (a.crane === true) out.push(line('Crane Delivery & Set', 1, 'ea', num((ctx.config || {}).crane_delivery_cost)))
      if (a.dirt === 'haul') out.push(line('Haul Excavated Dirt', num(a.haul_loads) || 1, 'load', priceOf(P, 'fiberglass_extra', 'Haul Excavated Dirt (per load)')))
      if (a.backfill === 'pea') out.push(line('Backfill - Pea Gravel', 1, 'ea', num((ctx.config || {}).pea_gravel_flat_price)))
      if (a.backfill === 'stone') out.push(line('Backfill - Stone', 1, 'ea', priceOf(P, 'fiberglass_extra', 'Backfill - Pea Gravel / Stone')))
      if (num(a.silt_ft) > 0) out.push(line('Silt Fence', num(a.silt_ft), 'ln ft', priceOf(P, 'fiberglass_extra', 'Silt Fence (per ln ft)')))
      if (a.spa === 'spillover') out.push(line('Spa - Spillover', 1, 'ea', priceOf(P, 'fiberglass_extra', 'Spa - Spillover')))
      if (a.spa === 'standalone') out.push(line('Spa - Standalone', 1, 'ea', priceOf(P, 'fiberglass_extra', 'Spa - Standalone')))
      var picked = a.addons || {}
      ;(ctx.addons || []).forEach(function(ad) {
        var v = picked[ad.id]
        if (v == null || v === false) return
        if (ad.pricing_type === 'flat') { if (v) out.push(addonLine(ad, 1)) }
        else if (num(v) > 0) out.push(addonLine(ad, v))
      })
      if (a.permit === 'us') out.push(line('Permit Handling', 1, 'ea', priceOf(P, 'fiberglass_extra', 'Permit Handling')))
      if (num(a.barrier_ft) > 0) out.push(line('Barrier / Fence', num(a.barrier_ft), 'ln ft', priceOf(P, 'fiberglass_extra', 'Barrier / Fence (per ln ft)')))
      if (a.permit === 'homeowner') out.push(noteLine('Scope: permit pulled by homeowner.'))
      return out
    }
    case 'landscaping': {
      var sc = a.scope || []
      if (sc.indexOf('restoration') >= 0) out.push(line('Restoration After Install', 1, 'ea', priceOf(P, 'landscape', 'Restoration After Install')))
      if (num(a.grading_sqft) > 0) out.push(line('Grading', num(a.grading_sqft), 'sq ft', priceOf(P, 'landscape', 'Grading (per sq ft)')))
      if (num(a.topsoil_yd) > 0) out.push(line('Topsoil', num(a.topsoil_yd), 'yd', priceOf(P, 'landscape', 'Topsoil (per yard)')))
      if (a.cover === 'seed' && num(a.cover_sqft) > 0) out.push(line('Seed', num(a.cover_sqft), 'sq ft', priceOf(P, 'landscape', 'Seed (per sq ft)')))
      if (a.cover === 'sod' && num(a.cover_sqft) > 0) out.push(line('Sod', num(a.cover_sqft), 'sq ft', priceOf(P, 'landscape', 'Sod (per sq ft)')))
      if (sc.indexOf('wall') >= 0 && num(a.wall_ft) > 0) {
        var rw = addonByName(ctx, 'Retaining Wall (Redi Rock)')
        out.push(line('Retaining Wall (Redi Rock)', num(a.wall_ft), 'ln ft', rw ? num(rw.customer_price) : null))
      }
      if ((a.deck_type === 'concrete' || a.deck_type === 'paver') && num(a.deck_sqft) > 0) {
        var dk = addonByName(ctx, a.deck_type === 'concrete' ? 'Concrete Decking' : 'Paver Decking')
        out.push(line(a.deck_type === 'concrete' ? 'Concrete Decking' : 'Paver Decking', num(a.deck_sqft), 'sq ft', dk ? num(dk.customer_price) : null))
      }
      if (sc.indexOf('plantings') >= 0) out.push(line('Plantings (allowance)', 1, 'ea', priceOf(P, 'landscape', 'Plantings (allowance)')))
      if (a.drainage === 'swale') out.push(line('Drainage - Swale', 1, 'ea', priceOf(P, 'landscape', 'Drainage - Swale')))
      if (a.drainage === 'french') out.push(line('Drainage - French Drain', num(a.french_ft), 'ln ft', priceOf(P, 'landscape', 'Drainage - French Drain (per ln ft)')))
      if (sc.indexOf('surround') >= 0) out.push(noteLine('Scope: full pool surround.'))
      return out
    }
    default: return out
  }
}

export function sheetTotal(lines) {
  return (lines || []).reduce(function(s, l) { return s + num(l.total_cost) }, 0)
}

export function jobNameFor(type, answers, ctx) {
  var a = answers || {}
  if (type === 'ag_liner' && a.size) return a.size + ' liner replacement'
  if (type === 'ag_install' && a.size) return a.size + (a.existing === 'teardown' ? ' tear down & install' : ' install')
  if (type === 'ig_liner') return 'Inground liner replacement'
  if (type === 'fiberglass') {
    var m = (ctx.models || []).find(function(x) { return x.id === a.model_id })
    return m ? m.model_name + ' fiberglass install' : 'Fiberglass install'
  }
  if (type === 'landscaping') return 'Landscaping'
  return ''
}

// What gets saved to bid_lines (drops UI-only flags)
export function toBidLineRows(lines) {
  return (lines || []).map(function(l) {
    return { description: l.description, category: l.category, quantity: l.quantity, unit: l.unit, unit_cost: l.unit_cost, total_cost: l.total_cost }
  })
}
